import { useMemo } from 'react';
import { findOrgPricingGaps, type OrgPricingGaps } from '@iomi/shared';
import type { QuoteType } from '../types/admin';
import { useOrgSettings } from './useOrgSettings';
import { useOrgRates } from './useOrgRates';
import { useOrgInventory } from './useOrgInventory';

/**
 * Whether the org is missing crew rates and/or packing item prices for the
 * move types a new estimate could be. `gaps` stays null while loading and when
 * any read fails (no warning beats a wrong one).
 */
export function useOrgPricingGaps(orgId: string, moveTypes: QuoteType[]): { gaps: OrgPricingGaps | null; loading: boolean } {
  const { org, loading: orgLoading, error: orgError } = useOrgSettings(orgId);
  const { rates, loading: ratesLoading, error: ratesError } = useOrgRates(orgId);
  const { items, loading: itemsLoading, error: itemsError } = useOrgInventory(orgId);
  const loading = orgLoading || ratesLoading || itemsLoading;
  const typesKey = moveTypes.join(',');

  const gaps = useMemo(() => {
    if (!orgId || loading || orgError || ratesError || itemsError || !org) return null;
    return findOrgPricingGaps({
      moveTypes,
      rates,
      useBlendedTruckDriverRate: org.useBlendedTruckDriverRate,
      residentialPricingModel: org.residentialPricingModel,
      items,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, loading, orgError, ratesError, itemsError, org, rates, items, typesKey]);

  return { gaps, loading };
}
